'use client';

import { ReactNode, useState } from 'react';

import { useParams, useRouter, useSearchParams } from 'next/navigation';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichText,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Text,
  Textarea,
} from '@/ui';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Place } from '@types';
import { useForm } from 'react-hook-form';
import slugify from 'slugify';
import { toast } from 'sonner';
import z, { ZodRawShape } from 'zod';

import { AtreemoBlockData } from '.';
import { DateOfBirthField } from './date-of-birth-field';
import { onSubmit } from './on-submit';
import { AtreemoFormPlaceSelector } from './place-selector';

export function AtreemoClientForm({
  places,
  additionalFields,
  thirdPartyOptIns,
  contactDataSource,
  useSelectedAsPreferred,
  marketingText,
  thirdPartyPolicies,
  metadata,
  id,
  successDialog,
}: { places: Place[]; id?: string } & AtreemoBlockData) {
  const phoneRegExp = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;

  const searchParams = useSearchParams();
  const params = useParams();
  const venuesPageSlug = params.venuesPage as string;
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const successQuery = new URLSearchParams(searchParams);
  successQuery.set('signup-success', 'true');

  const formSchema = z
    .object({
      FirstName: z.string().min(1, { message: 'Please enter your first name.' }),
      LastName: z.string().min(1, { message: 'Please enter your last name.' }),
      Email: z.string().email('Please enter a valid email address.'),
      Phone: z
        .string()
        .regex(phoneRegExp, 'Phone number is not valid')
        .transform((val: string) => val.replace(/\s/g, ''))
        .or(z.literal('')),
      BirthDate: z
        .date()
        .min(new Date(1900, 0, 1), "You can't be that old...")
        .max(
          new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()),
          'You must be at least 18 years old.',
        ),
      SelectedLocationID: z.string(),
      EmailMarketing: z.boolean(),
      PhoneMarketing: z.boolean(),
      PrivacyPolicy: z.literal(true, {
        message: 'Please accept the privacy policy.',
      }),
    })
    .extend(
      additionalFields?.reduce((acc, { label, required }) => {
        if (required) {
          return { ...acc, [slugify(label, { strict: true })]: z.string().min(1, 'This field is required') };
        } else {
          return { ...acc, [slugify(label, { strict: true })]: z.string() };
        }
      }, {} as ZodRawShape) || {},
    )
    .extend(
      (thirdPartyOptIns ?? []).reduce((acc, { label }) => {
        return { ...acc, [slugify(label, { strict: true })]: z.boolean() };
      }, {} as ZodRawShape),
    )
    .extend(
      (thirdPartyPolicies ?? []).reduce((acc, { label }) => {
        return { ...acc, [slugify(label.content, { strict: true })]: z.boolean() };
      }, {} as ZodRawShape),
    )
    .refine((data) => !(data.PhoneMarketing && !data.Phone), {
      message: 'You need to provide a phone number for us to contact you.',
      path: ['Phone'],
    })
    .refine((data) => data.EmailMarketing || data.PhoneMarketing, {
      message: 'Either email marketing or phone marketing must be selected.',
      path: ['EmailMarketing', 'PhoneMarketing'],
    })
    .refine(
      (data) => {
        const optIns = (thirdPartyOptIns ?? []).map(({ label }) => slugify(label, { strict: true }));
        const policies = (thirdPartyPolicies ?? []).map(({ label }) => slugify(label.content, { strict: true }));

        // Check for each opt-in that is true, there is a corresponding policy that is also true.
        return optIns.every((optInField) => {
          if (data[optInField]) {
            // Find at least one policy that is true
            return policies.some((policyField) => data[policyField]);
          }

          return true;
        });
      },
      {
        message: 'If you opt in to third-party communications, you must accept their privacy policy.',
        path: (thirdPartyPolicies ?? []).map(({ label }) => slugify(label.content, { strict: true })),
      },
    );

  type FieldType = {
    name: keyof z.infer<typeof formSchema>;
    label: ReactNode;
    placeholder?: string;
    type?: string;
  };

  const [parent] = useAutoAnimate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      BirthDate: undefined,
      EmailMarketing: false,
      PhoneMarketing: false,
      PrivacyPolicy: false,
      FirstName: '',
      LastName: '',
      SelectedLocationID: places?.[0]?.apps?.atreemo?.id || '',
      Email: '',
      Phone: '',
      ...additionalFields?.reduce(
        (acc, { label }) => ({
          ...acc,
          [slugify(label, { strict: true })]: '',
        }),
        {},
      ),
      ...thirdPartyOptIns?.reduce(
        (acc, { label }) => ({
          ...acc,
          [slugify(label, { strict: true })]: false,
        }),
        {},
      ),
      ...thirdPartyPolicies?.reduce(
        (acc, { label }) => ({
          ...acc,
          [slugify(label.content, { strict: true })]: false,
        }),
        {},
      ),
    },
  });

  const currentPlace = places?.find((place) => place.apps.atreemo.id === form.getValues('SelectedLocationID'));

  return (
    <>
      <Form {...form}>
        <form
          id={id}
          ref={parent}
          onSubmit={form.handleSubmit(
            async (values) =>
              await onSubmit({
                currentPlace,
                metadata,
                values,
                additionalFields,
                contactDataSource,
                useSelectedAsPreferred,
                thirdPartyOptIns,
              })
                .then(() => {
                  setOpen(true);
                  router.replace(`?${successQuery.toString()}`, {
                    scroll: false,
                  });
                  form.reset();
                })
                .catch((err) => {
                  console.error(err);
                  toast.error(err.message);
                }),
            (errors) => console.log(errors),
          )}
          className="flex w-full max-w-lg flex-col gap-5 px-4"
        >
          {(
            [
              { name: 'FirstName', label: 'First Name *', placeholder: 'John' },
              { name: 'LastName', label: 'Last Name *', placeholder: 'Smith' },
              {
                name: 'Email',
                label: 'Email *',
                placeholder: 'john.smith@example.com',
              },
              { name: 'Phone', label: 'Phone' },
            ] satisfies FieldType[]
          ).map(({ label, name, placeholder }) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={placeholder} value={field.value as string} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}

          <DateOfBirthField form={form} />

          {additionalFields?.map(({ label, placeholder, type, required }) => {
            const Field = type === 'long-text' ? Textarea : Input;

            return (
              <FormField
                key={label}
                control={form.control}
                name={slugify(label, { strict: true })}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {label} {required && '*'}
                    </FormLabel>
                    <FormControl>
                      <Field {...field} placeholder={placeholder} value={field.value as string} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            );
          })}

          {places.length > 1 && (
            <FormField
              key={'SelectedLocationID'}
              name="SelectedLocationID"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Location *</FormLabel>
                  <FormControl>
                    <Select value={field.value as string} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your local" />
                      </SelectTrigger>
                      <SelectContent>
                        {places.map(({ apps, name }) => (
                          <SelectItem key={name} value={apps.atreemo.id}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          <AtreemoFormPlaceSelector places={places} form={form} currentPlace={currentPlace} />

          <div key={'marketing'} className="bg-muted/40 flex flex-col gap-5 rounded-lg p-4 py-5">
            <Text element="p">
              {marketingText ||
                "If you'd like to hear more about exclusive offers, events and promotions from us, please tell us how we can contact you."}
            </Text>

            <hr className="border-border" />

            {(
              [
                { name: 'EmailMarketing', label: 'By email from [place]' },
                { name: 'PhoneMarketing', label: 'By SMS from [place]' },
                ...(thirdPartyOptIns?.map(({ label }) => ({
                  name: slugify(label, { strict: true }),
                  label: label,
                })) ?? []),
              ] satisfies FieldType[]
            ).map(({ name, label }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <>
                    <FormItem className="flex-row items-center justify-between gap-5 space-y-0">
                      <FormLabel>{label.replace('[place]', currentPlace?.name || '')}</FormLabel>
                      <FormControl>
                        <Switch
                          {...field}
                          value={String(field.value)}
                          onCheckedChange={(value) => field.onChange(value)}
                          checked={field.value as boolean}
                        />
                      </FormControl>
                    </FormItem>
                    <FormMessage />
                  </>
                )}
              />
            ))}
          </div>

          <div key={'PrivacyPolicy'} className="bg-muted/40 flex flex-col gap-5 rounded-lg p-4 py-5">
            {(
              [
                {
                  name: 'PrivacyPolicy',
                  label: `I have read and accept the <a href={${`${
                    places?.length === 1 ? `/${venuesPageSlug}/${places[0].custom.slug}` : ''
                  }/privacy-policy`}}>Privacy Policy</a> *`,
                },
                ...(thirdPartyPolicies?.map(({ label }) => ({
                  name: slugify(label.content, { strict: true }),
                  label: label.content.replace('</p>', ' *</p>'),
                })) ?? []),
              ] satisfies FieldType[]
            ).map(({ name, label }) => (
              <FormField
                control={form.control}
                name={name}
                key={name}
                render={({ field }) => (
                  <>
                    <FormItem className="flex-row items-center justify-between gap-5 space-y-0">
                      <FormLabel>
                        <RichText className="prose-sm">{label}</RichText>
                      </FormLabel>
                      <FormControl>
                        <Switch
                          {...field}
                          value={String(field.value)}
                          onCheckedChange={(value) => field.onChange(value)}
                          checked={field.value as boolean}
                        />
                      </FormControl>
                    </FormItem>
                    <FormMessage />
                  </>
                )}
              />
            ))}
          </div>

          <Button size="lg" className="w-max place-self-center" type="submit">
            Submit
          </Button>
        </form>
      </Form>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{successDialog?.title || 'Thank you for signing up!'}</DialogTitle>
            <DialogDescription asChild>
              <RichText className="prose-sm text-muted-foreground!">
                {successDialog?.message?.content || "We'll be in touch with the latest news and offers."}
              </RichText>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              size={'sm'}
              // variant={'outline'}
              onClick={() => setOpen(false)}
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
